import React, { useCallback, useEffect, useState } from 'react';
import { fetchEntitlements, openBillingPortal } from '../services/api';
import { Button, Card, Loading, ErrorText, errorMessage } from '../components/ui';
import { useMe } from '../hooks/useMe';

// Who you are, which organisation you are in, what you are paying for and how
// much of it you have used.
//
// Separate from Settings on purpose. Settings is what the product does - file
// types, tags, report logo, AI rates - and is edited routinely. This is the
// commercial relationship, read far more often than it is changed, and mixing
// the two buried "what plan am I on" under a list of upload categories.

const DIMENSION_LABELS = {
  projects: 'Projects',
  agents: 'Agents',
  users: 'Users',
};

// A bar per limit, so "3 of 3" is legible at a glance rather than needing to
// be compared in the reader's head. An unlimited dimension has no bar at all -
// a full-width bar would read as "at capacity", which is the opposite.
function UsageRow({ name, usage }) {
  const limit = usage?.limit;
  const current = Number(usage?.current) || 0;
  const unlimited = limit === null || limit === undefined;
  const pct = unlimited ? 0 : Math.min(100, Math.round((current / Math.max(1, limit)) * 100));

  return (
    <div className="usage-row">
      <div className="usage-head">
        <span className="usage-name">{name}</span>
        <span className={usage?.atLimit ? 'usage-count is-full' : 'usage-count'}>
          {unlimited ? `${current} · unlimited` : `${current} of ${limit}`}
        </span>
      </div>
      {unlimited ? null : (
        <div className="usage-track">
          <div
            className={usage?.atLimit ? 'usage-fill is-full' : 'usage-fill'}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  );
}

function BillingCard() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function open() {
    setBusy(true);
    setError('');
    try {
      const data = await openBillingPortal(window.location.pathname);
      if (data?.url) { window.location.href = data.url; return; }
      setError(data?.reason === 'stripe_not_configured'
        ? "Billing isn't configured on this deployment yet."
        : data?.details || 'Could not open the billing portal.');
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(false); }
  }

  return (
    <Card
      title="Billing"
      hint="Change plan, update your card, download invoices or cancel. Opens Stripe, and returns you here afterwards."
    >
      <ErrorText>{error}</ErrorText>
      <Button compact secondary onClick={open} disabled={busy}>
        {busy ? 'Opening…' : 'Manage billing'}
      </Button>
    </Card>
  );
}

export default function AccountPage() {
  const { me, isAdmin } = useMe();
  const [entitlements, setEntitlements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try { setEntitlements(await fetchEntitlements()); } catch (err) { setError(errorMessage(err)); }
  }, []);

  useEffect(() => { setLoading(true); load().finally(() => setLoading(false)); }, [load]);

  const dimensions = entitlements?.dimensions || {};
  const features = Object.entries(entitlements?.features || {}).filter(([, on]) => on);

  return (
    <>
      <h1>Account</h1>
      <p className="sub">Your organisation, plan and usage.</p>
      <ErrorText>{error}</ErrorText>

      <Card title="You">
        <div className="detail-list">
          <div className="detail-row"><span>Name</span><span>{me?.name || '—'}</span></div>
          <div className="detail-row"><span>Email</span><span>{me?.email || '—'}</span></div>
          <div className="detail-row"><span>Role</span><span>{me?.role || '—'}</span></div>
          {me?.org ? <div className="detail-row"><span>Organisation</span><span>{me.org}</span></div> : null}
        </div>
      </Card>

      {loading ? <Loading text="Loading plan…" rows={3} /> : (
        <>
          <Card
            title="Plan"
            hint={isAdmin ? undefined : 'Only an owner or admin can change the plan.'}
          >
            <div className="detail-list">
              <div className="detail-row">
                <span>Current plan</span>
                <span className="plan-tag">{entitlements?.planName || entitlements?.plan || '—'}</span>
              </div>
            </div>
            {features.length ? (
              <>
                <div className="field-name" style={{ marginTop: 14 }}>Included</div>
                <ul className="plan-list">
                  {features.map(([key]) => (
                    <li key={key} className="plan-feature">
                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase())}
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </Card>

          <Card title="Usage" hint="Counted against your plan's limits. Reaching one blocks new items of that kind, not the rest of the app.">
            {Object.keys(dimensions).length
              ? Object.entries(dimensions).map(([key, usage]) => (
                <UsageRow key={key} name={DIMENSION_LABELS[key] || key} usage={usage} />
              ))
              : <p className="muted" style={{ marginTop: 0 }}>No limits reported for this plan.</p>}
          </Card>
        </>
      )}

      {/* Billing is the owner's and admins' business - a member has nothing to
          do here and no permission to do it. */}
      {isAdmin ? <BillingCard /> : null}
    </>
  );
}
